import CategoryDetails = Office.CategoryDetails;
import AsyncResultStatus = Office.AsyncResultStatus;

class Outlook {
    public static getMessageMode(ctx: Office.Context) {
        if (ctx.mailbox.item)
            return "inReplyTo" in ctx.mailbox.item
                ? MessageMode.Compose
                : MessageMode.Read;
        return undefined;
    }

    public static setInLocalStorage(key: string, value: any) {
        const myPartitionKey = Office.context.partitionKey;

        const valueStr = JSON.stringify(value);

        // Check if local storage is partitioned. 
        // If so, use the partition to ensure the data is only accessible by your add-in.
        if (myPartitionKey) {
            localStorage.setItem(myPartitionKey + key, valueStr);
        } else {
            localStorage.setItem(key, valueStr);
        }
    }

    public static getFromLocalStorage<T>(key: string): T | undefined {
        const myPartitionKey = Office.context.partitionKey;

        // Check if local storage is partitioned.
        const valueStr = myPartitionKey
            ? localStorage.getItem(myPartitionKey + key)
            : localStorage.getItem(key);

        if (valueStr)
            return JSON.parse(valueStr) as T;
        else
            return null;
    }

    public static async loadItemById(ewsId: string) {
        return new Promise<Office.LoadedMessageCompose | Office.LoadedMessageRead>((resolve, reject) => {
            Office.context.mailbox.loadItemByIdAsync(ewsId, asyncResult => {
               if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                   resolve(asyncResult.value);
               } else {
                   reject(asyncResult);
               }
            });
        });
    }

    public static async unloadItem(message: Office.LoadedMessageRead | Office.LoadedMessageCompose) {
        return new Promise((resolve, reject) => {
            message.unloadAsync(asyncResult => {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(asyncResult);
                } else {
                    reject(asyncResult);
                }
            });
        });
    }

    public static async addFiledCategoryToCurrentSelection() {
        return new Promise<void>((resolve, reject ) => {
            Office.context.mailbox.getSelectedItemsAsync(async (getSelectedItemsResult) => {
                if (getSelectedItemsResult.status == Office.AsyncResultStatus.Failed) {
                    reject(getSelectedItemsResult);
                    return;
                }

                const item = await this.loadItemById(getSelectedItemsResult.value[0].itemId);
                await Outlook.addCategory(item as any, 'Filed');
                await this.unloadItem(item);
                resolve();
            });
        });
    }

    public static async removeFiledCategoryToCurrentSelection() {
        return new Promise<void>((resolve, reject) => {
            Office.context.mailbox.getSelectedItemsAsync(async (getSelectedItemsResult) => {
                if (getSelectedItemsResult.status == Office.AsyncResultStatus.Failed) {
                    reject(getSelectedItemsResult);
                    return;
                }

                const item = await this.loadItemById(getSelectedItemsResult.value[0].itemId);
                await Outlook.removeCategory(item as any, 'Filed');
                await this.unloadItem(item);
                resolve();
            });
        });
    }

    public static async addCategory (mailboxItem: Office.MessageRead, categoryName: string) {
        return new Promise<void>((resolve, reject) => {
            mailboxItem.categories.addAsync([categoryName], (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    console.log(`Failed to add category: ${categoryName} to the item. ${asyncResult.error.name}: ${asyncResult.error.code} - ${asyncResult.error.message}`);
                    reject(asyncResult);
                }
                resolve();
            });
        });
    };

    public static async removeCategory (mailboxItem: Office.MessageRead, categoryName: string) {
        return new Promise<void>((resolve, reject) => {
            mailboxItem.categories.removeAsync([categoryName], (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    console.log(`Failed to remove category: ${categoryName} to the item. ${asyncResult.error.name}: ${asyncResult.error.code} - ${asyncResult.error.message}`);
                    reject(asyncResult);
                }
                resolve();
            });
        });
    };

    public static async getSelectedItems() {
        return new Promise<Office.SelectedItemDetails[]>((resolve, reject) => {
           try {
               Office.context.mailbox.getSelectedItemsAsync(asyncResult => {
                   if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                       resolve(asyncResult.value);
                   } else {
                       reject(asyncResult);
                   }
               });
           } catch (e) {
               reject();
           }
        });
    }
}

export enum MessageMode {
    Read, Compose
}

export default Outlook;